import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { defaultValue, IApprovisonnement } from '../../shared/model/appro.model';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IEcole } from 'app/shared/model/ecole.model';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IApprovisonnement>,
  entity: defaultValue,
  solde: 0,
  updating: false,
  totalItems: 0,
  soldeTotal: null,
};

const apiUrl = 'api/approvisonnement';
const apiUrlSolde = 'api/comptes/solde';

const getEcoleApprovisonnement = async approvisonnement => {
  const ecoleResponse = await axios.get<IEcole>(`/api/ecoles/${approvisonnement.ecoleId}`);
  const ecoleData = ecoleResponse.data;
  return {
    ...approvisonnement,
    ecole: ecoleData,
  };
};

export const getEcoleApprovisonnements = createAsyncThunk(
  'approvisonnement/fetch_ecole_approvisonnement_list',
  async (entity: IApprovisonnement) => {
    let requestUrl = '';
    let typeApprovisonnement;
    let statutApprovisonnement;
    let searchUrl = '';
    if (entity.typeApprovisonnement) {
      typeApprovisonnement = '&typeApprovisonnement=' + entity.typeApprovisonnement;
      searchUrl += typeApprovisonnement;
    }
    if (entity.statutApprovisonnement) {
      statutApprovisonnement = '&statutApprovisonnement=' + entity.statutApprovisonnement;
      searchUrl += statutApprovisonnement;
    }
    if (!entity.typeApprovisonnement && !entity.statutApprovisonnement) {
      requestUrl = `${apiUrl}/school-list?anneeScolaireId=${entity.anneeScolaireId}&page=${entity.page}&size=${entity.size}`;
    } else {
      requestUrl = `${apiUrl}/school-list?anneeScolaireId=${entity.anneeScolaireId}&page=${entity.page}&size=${entity.size}${
        searchUrl ? '&' : '?'
      }${searchUrl}`;
    }
    const response = await axios.get<IApprovisonnement[]>(requestUrl);
    const totalItems = response.headers['x-total-count'];
    return { result: response.data, totalItems };
  }
);

export const rechargeBySchoolAccount = createAsyncThunk(
  'approvisonnement/rechargeBySchoolAccount',
  async (entity: IApprovisonnement, thunkAPI) => {
    const result = await axios.post<IApprovisonnement>(`${apiUrl}/rechargeBySchoolAccount`, entity);
    thunkAPI.dispatch(getEcoleApprovisonnements(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const rechargeByBankAccount = createAsyncThunk(
  'approvisonnement/rechargeByBankAccount',
  async (formData: FormData) => {
    const result = await axios.post<IApprovisonnement>(`${apiUrl}/rechargeByBankAccount`, formData);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const validateRequest = createAsyncThunk(
  'approvisonnement/validate_approvisonnement',
  async (entity: IApprovisonnement, thunkAPI) => {
    if (entity.id === undefined) {
      return thunkAPI.rejectWithValue('ID is undefined');
    }
    const result = await axios.put<IApprovisonnement>(`${apiUrl}/validate/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getApprovisonnement(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const rejectedRequest = createAsyncThunk(
  'approvisonnement/rejected_approvisonnement',
  async (entity: IApprovisonnement, thunkAPI) => {
    if (entity.id === undefined) {
      return thunkAPI.rejectWithValue('ID is undefined');
    }
    const result = await axios.put<IApprovisonnement>(`${apiUrl}/reject/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getApprovisonnement(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateApprovisionnement = createAsyncThunk(
  'approvisonnement/update_approvisonnement',
  async (entity: IApprovisonnement, thunkAPI) => {
    if (entity.id === undefined) {
      return thunkAPI.rejectWithValue('ID is undefined');
    }
    const result = await axios.put<IApprovisonnement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getApprovisonnement(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getApprovisonnement = createAsyncThunk(
  'approvisonnement/fetch_approvisonnement',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IApprovisonnement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getSoldeCompteScolarite = createAsyncThunk(
  'solde',
  async (ecoleId: string | number) => {
    const requestUrl = `${apiUrlSolde}/${ecoleId}`;
    const result = await axios.get(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getAllApprovisonnements = createAsyncThunk('approvisonnement/fetch_approvisonnement_list', async (entity: any) => {
  let requestUrl = '';
  let statutApprovisonnement;
  let searchUrl = '';
  if (entity.statutApprovisonnement) {
    statutApprovisonnement = '&statutApprovisonnement=' + entity.statutApprovisonnement;
    searchUrl += statutApprovisonnement;
  }
  if (!entity.statutApprovisonnement) {
    requestUrl = `${apiUrl}/list?page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${apiUrl}/list?page=${entity.page}&size=${entity.size}${searchUrl ? '&' : '?'}${searchUrl}`;
  }

  const response = await axios.get<IApprovisonnement[]>(requestUrl);
  const totalItems = response.headers['x-total-count'];
  return { result: response.data, totalItems };
});

export const soldeTotalByEcoleSalaire = createAsyncThunk(
  'approvisonnement/get_solde_total',
  async (entity: IApprovisonnement, thunkAPI) => {
    const result = await axios.get(`${apiUrl}/salaires/solde/${entity.ecoleId}`);
    thunkAPI.dispatch(getEcoleApprovisonnements(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// Création du slice Redux
export type ApprovisonnementState = Readonly<typeof initialState>;
const ApprovisonnementSlice = createSlice({
  name: 'approvisonnement',
  initialState: initialState as ApprovisonnementState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getApprovisonnement.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEcoleApprovisonnements), (state, action) => {
        state.loading = false;
        state.entities = action.payload.result;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(getSoldeCompteScolarite), (state, action) => {
        state.loading = false;
        state.solde = action.payload.data;
      })
      .addMatcher(isFulfilled(getAllApprovisonnements), (state, action) => {
        state.loading = false;
        state.entities = action.payload.result;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(rechargeBySchoolAccount), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(rechargeByBankAccount), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(soldeTotalByEcoleSalaire), (state, action) => {
        state.loading = false;
        state.soldeTotal = action.payload.data;
      })
      .addMatcher(
        isRejected(
          getEcoleApprovisonnements,
          getAllApprovisonnements,
          soldeTotalByEcoleSalaire,
          rechargeByBankAccount,
          rechargeBySchoolAccount
        ),
        (state, action) => {
          state.loading = false;
        }
      )
      .addMatcher(
        isPending(
          getEcoleApprovisonnements,
          getAllApprovisonnements,
          getApprovisonnement,
          soldeTotalByEcoleSalaire,
          getSoldeCompteScolarite
        ),
        state => {
          state.errorMessage = null;
          state.loading = true;
        }
      )
      .addMatcher(isPending(rechargeBySchoolAccount, rechargeByBankAccount), state => {
        state.errorMessage = null;
        state.updating = true;
      });
  },
});

export const { reset } = ApprovisonnementSlice.actions;

export default ApprovisonnementSlice.reducer;
